import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Download,
  FilePlus2,
  FileSignature,
  Loader2,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import ContractSignaturePanel from "@/components/contracts/ContractSignaturePanel";
import { api, formatApiError } from "@/lib/api";
import { SignedLink } from "@/lib/signedFile";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";

const KIND_LABEL = {
  ASSOCIATION_AGREEMENT: "اتفاقية جمعية",
  STUDENT_COMMITMENT: "تعهد طالب",
};

export const CONTRACT_STATE_LABELS = {
  DRAFT: "بانتظار استكمال بيانات الجمعية",
  PENDING_SIGNATURES: "بانتظار التوقيعات",
  PARTIALLY_SIGNED: "موقّع جزئيًا",
  FINALIZING: "جارٍ إقفال الوثيقة",
  FINALIZATION_FAILED: "تعذر إقفال الوثيقة",
  ACTIVE: "سارية",
  AWAITING_STUDENT_SIGNATURE: "بانتظار توقيع الطالب",
  SIGNED: "مكتملة التوقيع",
  EXPIRED: "منتهية",
  TERMINATED: "منتهية إداريًا",
  SUPERSEDED: "مستبدلة",
  VOID: "ملغاة",
};

const STATE_TONES = {
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  SIGNED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PENDING_SIGNATURES: "bg-amber-50 text-amber-800 border-amber-200",
  AWAITING_STUDENT_SIGNATURE: "bg-amber-50 text-amber-800 border-amber-200",
  PARTIALLY_SIGNED: "bg-amber-50 text-amber-800 border-amber-200",
  FINALIZING: "bg-sky-50 text-sky-700 border-sky-200",
  FINALIZATION_FAILED: "bg-rose-50 text-rose-700 border-rose-200",
  EXPIRED: "bg-slate-100 text-slate-600 border-slate-200",
  TERMINATED: "bg-slate-100 text-slate-600 border-slate-200",
  SUPERSEDED: "bg-slate-100 text-slate-600 border-slate-200",
  VOID: "bg-slate-100 text-slate-500 border-slate-200",
};

const ASSOC_AGREEMENT_FIELDS = [
  { key: "project_name", label: "اسم المشروع/البرنامج", placeholder: "مثال: برنامج التدريب التعاوني — إدارة برامج التنمية" },
  { key: "association_name", label: "اسم الجمعية الرسمي", placeholder: "يُملأ تلقائيًا من ملف الجمعية" },
  { key: "association_license_number", label: "رقم الترخيص", placeholder: "1234" },
  { key: "association_address", label: "عنوان الجمعية", placeholder: "الرياض، المملكة العربية السعودية" },
  { key: "association_representative_name", label: "اسم ممثل الجمعية", placeholder: "الاسم الكامل" },
  { key: "association_representative_title", label: "صفة ممثل الجمعية", placeholder: "الرئيس التنفيذي" },
];

const ASSOC_DATE_FIELDS = [
  { key: "agreement_signing_date", label: "تاريخ توقيع الاتفاقية" },
  { key: "agreement_start_date", label: "تاريخ بداية السريان" },
  { key: "agreement_end_date", label: "تاريخ نهاية السريان" },
];

function StateBadge({ state }) {
  const tone = STATE_TONES[state] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${tone}`}>
      {CONTRACT_STATE_LABELS[state] || state || "—"}
    </span>
  );
}

function formatDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("ar-SA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatShortDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("ar-SA", { dateStyle: "medium" }).format(new Date(value));
}

export default function AdminContracts() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ kind: "", state: "", search: "" });
  const [issueOpen, setIssueOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const linked = searchParams.get("document");
    if (linked) { setOpenId(linked); }
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, usersRes] = await Promise.all([
        api.get("/contracts/documents"),
        api.get("/users", { params: { role: "association" } }),
      ]);
      setDocuments(docsRes.data.documents || []);
      setAssociations(usersRes.data.users || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const counts = {};
    for (const doc of documents) {
      counts[doc.state] = (counts[doc.state] || 0) + 1;
    }
    return counts;
  }, [documents]);

  const filtered = useMemo(() => {
    const term = filters.search.trim();
    return documents.filter((doc) => {
      if (filters.kind && doc.kind !== filters.kind) return false;
      if (filters.state && doc.state !== filters.state) return false;
      if (term) {
        const bag = [doc.number, doc.title, doc.association_id, doc.student_id]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase();
        if (!bag.includes(term.toLocaleLowerCase())) return false;
      }
      return true;
    });
  }, [documents, filters]);

  const assocById = useMemo(() => {
    const map = new Map();
    associations.forEach((a) => map.set(a.id, a));
    return map;
  }, [associations]);

  return (
    <DashboardLayout>
      <div data-testid="admin-contracts" dir="rtl">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">إدارة العقود</div>
            <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">العقود</h1>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-600">
              تشغيل اتفاقيات الجمعيات وتعهدات الطلاب: الإصدار، متابعة الحالة، توقيع الجمعية، وإقفال الوثائق.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="contracts-refresh"
            >
              <RefreshCw className="h-4 w-4" />
              تحديث
            </button>
            <button
              type="button"
              onClick={() => setIssueOpen(true)}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-teal-700 px-4 text-sm font-bold text-white hover:bg-teal-600"
              data-testid="issue-association-agreement"
            >
              <FilePlus2 className="h-4 w-4" />
              إصدار اتفاقية جمعية
            </button>
          </div>
        </div>

        <StatsRow stats={stats} total={documents.length} />

        <FiltersBar filters={filters} setFilters={setFilters} />

        {loading ? (
          <div className="grid place-items-center py-24" data-testid="contracts-loading">
            <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="grid place-items-center rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">
            <FileSignature className="h-10 w-10 text-slate-300" />
            <div className="mt-3 text-sm">لا توجد عقود مطابقة للفلاتر الحالية.</div>
          </div>
        ) : (
          <ContractsTable
            rows={filtered}
            assocById={assocById}
            onOpen={(id) => setOpenId(id)}
          />
        )}

        {issueOpen && (
          <IssueAssociationAgreementModal
            associations={associations}
            onClose={() => setIssueOpen(false)}
            onIssued={async (docId) => {
              setIssueOpen(false);
              await load();
              if (docId) setOpenId(docId);
            }}
          />
        )}

        {openId && (
          <ContractDetailDrawer
            documentId={openId}
            currentUser={user}
            associations={assocById}
            onClose={() => setOpenId(null)}
            onChanged={load}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function StatsRow({ stats, total }) {
  const cards = [
    { key: "ACTIVE", label: CONTRACT_STATE_LABELS.ACTIVE, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { key: "PENDING_SIGNATURES", label: CONTRACT_STATE_LABELS.PENDING_SIGNATURES, tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { key: "PARTIALLY_SIGNED", label: CONTRACT_STATE_LABELS.PARTIALLY_SIGNED, tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { key: "FINALIZATION_FAILED", label: CONTRACT_STATE_LABELS.FINALIZATION_FAILED, tone: "bg-rose-50 text-rose-700 border-rose-200" },
    { key: "AWAITING_STUDENT_SIGNATURE", label: CONTRACT_STATE_LABELS.AWAITING_STUDENT_SIGNATURE, tone: "bg-sky-50 text-sky-700 border-sky-200" },
  ];
  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <div className="rounded-2xl border border-teal-200 bg-teal-50 p-4">
        <div className="text-xs font-semibold text-teal-700">إجمالي العقود</div>
        <div className="mt-1 text-2xl font-extrabold text-slate-900">{total}</div>
      </div>
      {cards.map((card) => (
        <div key={card.key} className={`rounded-2xl border p-4 ${card.tone}`}>
          <div className="text-xs font-semibold">{card.label}</div>
          <div className="mt-1 text-2xl font-extrabold">{stats[card.key] || 0}</div>
        </div>
      ))}
    </div>
  );
}

function FiltersBar({ filters, setFilters }) {
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={filters.search}
          onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
          placeholder="ابحث برقم العقد أو المعرف"
          className="min-h-11 w-full rounded-xl border border-slate-300 bg-white pr-9 pl-3 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
          data-testid="contracts-search"
        />
      </div>
      <select
        value={filters.kind}
        onChange={(event) => setFilters((current) => ({ ...current, kind: event.target.value }))}
        className="min-h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700"
        data-testid="contracts-filter-kind"
      >
        <option value="">كل الأنواع</option>
        {Object.entries(KIND_LABEL).map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
      <select
        value={filters.state}
        onChange={(event) => setFilters((current) => ({ ...current, state: event.target.value }))}
        className="min-h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700"
        data-testid="contracts-filter-state"
      >
        <option value="">كل الحالات</option>
        {Object.entries(CONTRACT_STATE_LABELS).map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
    </div>
  );
}

function ContractsTable({ rows, assocById, onOpen }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="min-w-full divide-y divide-slate-200 text-sm" data-testid="contracts-table">
        <thead className="bg-slate-50 text-right text-xs font-bold uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-4 py-3">الرقم</th>
            <th className="px-4 py-3">النوع</th>
            <th className="px-4 py-3">الجمعية</th>
            <th className="px-4 py-3">الحالة</th>
            <th className="px-4 py-3">التاريخ</th>
            <th className="px-4 py-3 text-left">إجراء</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((doc) => {
            const assoc = doc.association_id ? assocById.get(doc.association_id) : null;
            return (
              <tr key={doc.id} className="hover:bg-slate-50/60">
                <td className="px-4 py-3 font-mono text-xs text-slate-800" dir="ltr">
                  {doc.number || doc.id.slice(-8)}
                </td>
                <td className="px-4 py-3 text-slate-700">{KIND_LABEL[doc.kind] || doc.kind}</td>
                <td className="px-4 py-3 text-slate-700">
                  {assoc?.organization_name || assoc?.name || <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3"><StateBadge state={doc.state} /></td>
                <td className="px-4 py-3 text-slate-500">{formatShortDate(doc.created_at)}</td>
                <td className="px-4 py-3 text-left">
                  <button
                    type="button"
                    onClick={() => onOpen(doc.id)}
                    className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-slate-900 px-3 text-xs font-bold text-white hover:bg-slate-800"
                    data-testid={`contract-open-${doc.id}`}
                  >
                    فتح
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function IssueAssociationAgreementModal({ associations, onClose, onIssued }) {
  const [associationId, setAssociationId] = useState("");
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const selected = useMemo(
    () => associations.find((a) => a.id === associationId) || null,
    [associations, associationId],
  );

  // Auto-fill association_name when association is chosen.
  useEffect(() => {
    if (selected && !values.association_name) {
      setValues((current) => ({
        ...current,
        association_name: selected.organization_name || selected.name || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associationId]);

  const setValue = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));

  const validate = () => {
    if (!associationId) return "اختر الجمعية أولًا";
    for (const field of ASSOC_AGREEMENT_FIELDS) {
      if (!String(values[field.key] || "").trim()) return `${field.label} مطلوب`;
    }
    for (const field of ASSOC_DATE_FIELDS) {
      if (!values[field.key]) return `${field.label} مطلوب`;
    }
    if (new Date(values.agreement_end_date) < new Date(values.agreement_start_date)) {
      return "تاريخ نهاية السريان يجب أن يكون بعد تاريخ البداية";
    }
    return null;
  };

  const submit = async () => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      const cleaned = {};
      for (const field of ASSOC_AGREEMENT_FIELDS) {
        cleaned[field.key] = String(values[field.key] || "").trim();
      }
      for (const field of ASSOC_DATE_FIELDS) {
        cleaned[field.key] = values[field.key];
      }
      const { data } = await api.post("/contracts/admin/association-agreements", {
        association_id: associationId,
        values: cleaned,
      });
      toast.success("تم إصدار اتفاقية الجمعية");
      onIssued(data.document?.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 grid place-items-center bg-slate-900/60 p-3 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl" dir="rtl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900">إصدار اتفاقية جمعية</h2>
            <p className="mt-1 text-xs text-slate-500">
              يعتمد الإصدار على القالب المنشور «اتفاقية الجمعية». راجع القالب من صفحة التكاملات إذا لزم.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-slate-100"
            aria-label="إغلاق"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <label className="block text-sm font-semibold text-slate-700">
            الجمعية
            <select
              value={associationId}
              onChange={(event) => setAssociationId(event.target.value)}
              className="mt-2 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
              data-testid="issue-association-select"
            >
              <option value="">— اختر الجمعية —</option>
              {associations.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.organization_name || a.name} — {a.email}
                </option>
              ))}
            </select>
          </label>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {ASSOC_AGREEMENT_FIELDS.map((field) => (
              <label key={field.key} className="block text-sm font-semibold text-slate-700">
                {field.label}
                <input
                  type="text"
                  value={values[field.key] || ""}
                  onChange={(event) => setValue(field.key, event.target.value)}
                  placeholder={field.placeholder}
                  className="mt-2 min-h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
                  data-testid={`issue-field-${field.key}`}
                />
              </label>
            ))}
            {ASSOC_DATE_FIELDS.map((field) => (
              <label key={field.key} className="block text-sm font-semibold text-slate-700">
                {field.label}
                <input
                  type="date"
                  value={values[field.key] || ""}
                  onChange={(event) => setValue(field.key, event.target.value)}
                  className="mt-2 min-h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
                  data-testid={`issue-date-${field.key}`}
                />
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            إلغاء
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-teal-700 px-5 text-sm font-bold text-white hover:bg-teal-600 disabled:opacity-60"
            data-testid="issue-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />}
            إصدار وحفظ الوثيقة
          </button>
        </div>
      </div>
    </div>
  );
}

function ContractDetailDrawer({ documentId, currentUser, associations, onClose, onChanged }) {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contracts/documents/${documentId}`);
      setDocument(data.document);
    } catch (error) {
      toast.error(formatApiError(error));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [documentId, onClose]);

  useEffect(() => {
    load();
  }, [load]);

  const assoc = document?.association_id ? associations.get(document.association_id) : null;

  const associationSignature = useMemo(
    () => (document?.signatures || []).find((s) => s.slot === "association") || null,
    [document],
  );
  const siaqSignature = useMemo(
    () => (document?.signatures || []).find((s) => s.slot === "siaq") || null,
    [document],
  );

  const canSignAsSiaq =
    document?.my_signer_slot === "siaq"
      && !siaqSignature
      && (document.kind !== "ASSOCIATION_AGREEMENT" || Boolean(associationSignature))
      && ["PENDING_SIGNATURES", "PARTIALLY_SIGNED"].includes(document.state);

  const waitingForAssociation =
    document?.my_signer_slot === "siaq"
      && document?.kind === "ASSOCIATION_AGREEMENT"
      && !associationSignature;

  const retryFinalize = async () => {
    setRetrying(true);
    try {
      await api.post(`/contracts/documents/${documentId}/retry-finalization`);
      toast.success("أُعيدت محاولة إقفال الوثيقة");
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex justify-start bg-slate-900/60 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside
        className="flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl"
        dir="rtl"
        data-testid="contract-detail-drawer"
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">
              {document ? KIND_LABEL[document.kind] : "..."}
            </div>
            <h2 className="mt-1 font-heading text-xl font-extrabold text-slate-900">
              {document?.title || "تفاصيل العقد"}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span dir="ltr" className="font-mono">{document?.number || documentId}</span>
              {document?.state && <StateBadge state={document.state} />}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-slate-100"
            aria-label="إغلاق"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading || !document ? (
            <div className="grid place-items-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
            </div>
          ) : (
            <div className="space-y-6">
              {assoc && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm">
                  <div className="text-xs font-semibold text-slate-500">الجمعية</div>
                  <div className="mt-1 font-bold text-slate-900">
                    {assoc.organization_name || assoc.name}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">{assoc.email}</div>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-3">
                <MetaCell label="أُنشئت في" value={formatDate(document.created_at)} />
                <MetaCell label="سريان من" value={formatShortDate(document.valid_from)} />
                <MetaCell label="سريان إلى" value={formatShortDate(document.valid_until)} />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 py-3 text-sm font-bold text-slate-800">
                  التوقيعات
                </div>
                <div className="divide-y divide-slate-100">
                  {(document.signatures || []).length === 0 && (
                    <div className="p-4 text-sm text-slate-500">لا توجد توقيعات بعد.</div>
                  )}
                  {(document.signatures || []).map((sig) => (
                    <div key={sig.id} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div>
                        <div className="text-sm font-bold text-slate-900">
                          {sig.slot === "siaq" ? "منصة سياق" : sig.slot === "association" ? "الجمعية" : "الطالب"}
                        </div>
                        <div className="text-xs text-slate-500">{sig.typed_name || "—"}</div>
                      </div>
                      <div className="text-xs text-slate-500">{formatDate(sig.signed_at)}</div>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" /> موثّق
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="mb-2 text-sm font-bold text-slate-800">نص الوثيقة الكامل</div>
                <div
                  className="max-h-[52vh] overflow-y-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm leading-8 text-slate-800"
                  dir="rtl"
                  data-testid="contract-rendered-text"
                >
                  {document.rendered_text || document.legal_text || "—"}
                </div>
              </div>

              {document.state === "FINALIZATION_FAILED" && currentUser?.role === "super_admin" && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
                    <div className="flex-1">
                      <div className="font-bold text-rose-800">تعذّر إقفال الوثيقة</div>
                      <div className="mt-1 text-xs text-rose-700">
                        {document.finalization_error || "خطأ داخلي أثناء توليد PDF أو رفع الملف."}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={retryFinalize}
                      disabled={retrying}
                      className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-rose-700 px-4 text-sm font-bold text-white hover:bg-rose-600 disabled:opacity-60"
                      data-testid="contract-retry-finalize"
                    >
                      {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      إعادة الإقفال
                    </button>
                  </div>
                </div>
              )}

              {waitingForAssociation && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    توقيع سياق غير متاح حتى توقع الجمعية أولًا. سنُنبّهك تلقائيًا عند وصول توقيع الجمعية.
                  </div>
                </div>
              )}

              {canSignAsSiaq && (
                <ContractSignaturePanel
                  documentId={documentId}
                  expectedName={currentUser?.name}
                  onSigned={async () => {
                    await load();
                    onChanged?.();
                  }}
                />
              )}

              {document.artifact_file_id && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-emerald-800">تنزيل النسخة النهائية</div>
                      <div className="mt-1 text-xs text-emerald-700">
                        الملف مُخزّن في المخزن الآمن، ويُخدَّم عبر رابط موقّع قصير الصلاحية.
                      </div>
                    </div>
                    <SignedLink
                      fileId={document.artifact_file_id}
                      filename={`${document.number}.pdf`}
                      className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-emerald-700 px-4 text-sm font-bold text-white hover:bg-emerald-600"
                      testid="contract-download"
                    >
                      <Download className="h-4 w-4" />
                      تنزيل PDF
                    </SignedLink>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-800">{value}</div>
    </div>
  );
}
